import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Update } from '../../services/service.api';
import { toast } from 'react-toastify';
import {
  User, Mail, Phone, Lock, Camera, Trash2,
  Save
} from 'lucide-react';

export default function Profile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector(s => s.userReducer.token);
  const user = useSelector(s => s.userReducer.user);


  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);

  
  // Personal Info Form
  const [form, setForm] = useState({
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    email: user?.email || '',
    tel: user?.tel || '',
    photo: user?.photo || '',
    percentage: user?.percentage || 0,
    current_password: '',
    new_password: '',
    confirm_password: '',
  });



  // Synchroniser quand l'utilisateur change
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        nom: user.nom || '',
        prenom: user.prenom || '',
        email: user.email || '',
        tel: user.tel || '',
        photo: user.photo || '',
        percentage: user.percentage || 0,
      }));
    }
  }, [user]);



  // Gestion du téléversement de la photo
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérification de la taille (max 5 Mo)
    if (file.size > 5 * 1024 * 1024) {
      return toast.error('L\'image est trop volumineuse (max 5 Mo)');
    }

    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const base64 = uploadEvent.target.result;
      setForm(prev => ({ ...prev, photo: base64 }));
      toast.info('Photo chargée. Cliquez sur "Enregistrer" pour confirmer.');
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setForm(prev => ({ ...prev, photo: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
    toast.info('Photo retirée. Cliquez sur "Enregistrer" pour confirmer.');
  };

  // Sauvegarde globale du profil
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) {
      return toast.error('Nom, prénom et email sont obligatoires');
    }

    if (form.new_password) {
      if (form.new_password.length < 6) {
        return toast.error('Le nouveau mot de passe doit faire au moins 6 caractères');
      }
      if (form.new_password !== form.confirm_password) {
        return toast.error('Les nouveaux mots de passe ne correspondent pas');
      }
    }

    setLoading(true);
    try {
      // 1. Mettre à jour l'utilisateur
      const updatePayload = {
        nom: form.nom,
        prenom: form.prenom,
        email: form.email,
        tel: form.tel,
        photo: form.photo,
        percentage: form.percentage,
      };

      if (form.new_password) {
        updatePayload.new_password = form.new_password;
        if (form.current_password) updatePayload.current_password = form.current_password;
      }

      const res = await Update(`/users/${user.id}`, updatePayload, token);

      if (res?.error) {
        return toast.error(res.error);
      }



      // Mettre à jour Redux et LocalStorage
      const updatedUser = res.user || { ...user, ...updatePayload };
      dispatch({ type: 'user-update', value: updatedUser });


      // Réinitialiser les champs mot de passe
      setForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));

      toast.success(t.profile.saveSuccess);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const isInvestor = user?.role === 'investisseur';
  const initials = `${(form.prenom || 'U')[0] || ''}${(form.nom || '')[0] || ''}`.toUpperCase();

  return (
    <div className="page-content page-enter">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>{t.profile.title}</h1>
          <p>{t.profile.subtitle}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`badge ${isInvestor ? 'badge-primary' : 'badge-gold'}`} style={{ fontSize: '0.85rem', padding: '6px 12px' }}>
            {isInvestor ? t.profile.roleInvestorBadge : t.profile.rolePartnerBadge}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid-2" style={{ alignItems: 'start', gap: 24 }}>
          
          {/* Colonne GAUCHE : Photo + Informations personnelles */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            
            {/* Carte Photo de Profil */}
            <div className="card">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Camera size={18} color="var(--primary)" />
                {t.profile.photoAvatar}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                {/* Avatar Preview */}
                <div style={{ position: 'relative' }}>
                  {form.photo ? (
                    <img
                      src={form.photo}
                      alt={`${form.prenom} ${form.nom}`}
                      style={{
                        width: 88,
                        height: 88,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid var(--primary)',
                        boxShadow: '0 4px 14px rgba(20, 184, 166, 0.25)',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 88,
                        height: 88,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--primary), var(--gold))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '2rem',
                        color: 'white',
                        border: '3px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                      }}
                    >
                      {initials || <User size={36} />}
                    </div>
                  )}
                </div>

                {/* Actions Photo */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Camera size={14} /> {t.profile.changePhoto}
                    </button>
                    {form.photo && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={handleRemovePhoto}
                        title={t.profile.removePhoto}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {t.profile.photoHint}
                  </div>
                </div>
              </div>
            </div>

            {/* Carte Informations Personnelles */}
            <div className="card">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <User size={18} color="var(--primary)" />
                {t.profile.personalInfo}
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">{t.auth.prenom} <span className="required">*</span></label>
                  <input
                    className="form-control"
                    value={form.prenom}
                    onChange={e => setForm({ ...form, prenom: e.target.value })}
                    required
                    placeholder="Prénom"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.auth.nom} <span className="required">*</span></label>
                  <input
                    className="form-control"
                    value={form.nom}
                    onChange={e => setForm({ ...form, nom: e.target.value })}
                    required
                    placeholder="Nom"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Mail size={14} style={{ display: 'inline', marginRight: 4 }} />
                  {t.auth.email} <span className="required">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="exemple@savantech.org"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Phone size={14} style={{ display: 'inline', marginRight: 4 }} />
                  {t.auth.tel}
                </label>
                <input
                  type="tel"
                  className="form-control"
                  value={form.tel}
                  onChange={e => setForm({ ...form, tel: e.target.value })}
                  placeholder="+226 XX XX XX XX"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Pourcentage des parts (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                  value={form.percentage}
                  onChange={e => setForm({ ...form, percentage: e.target.value })}
                  placeholder="Ex: 10"
                />
              </div>
            </div>

          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>


            {/* Carte Sécurité & Mot de passe */}
            <div className="card">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Lock size={18} color="var(--primary)" />
                {t.profile.security}
              </div>

              <div className="form-group">
                <label className="form-label">{t.profile.currentPassword}</label>
                <input
                  type="password"
                  className="form-control"
                  value={form.current_password}
                  onChange={e => setForm({ ...form, current_password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">{t.profile.newPassword}</label>
                  <input
                    type="password"
                    className="form-control"
                    value={form.new_password}
                    onChange={e => setForm({ ...form, new_password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.profile.confirmNewPassword}</label>
                  <input
                    type="password"
                    className="form-control"
                    value={form.confirm_password}
                    onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {t.profile.passwordHint}
              </div>
            </div>

          </div>
        </div>

        {/* Bouton de Sauvegarde Flottant / En bas */}
        <div style={{
          marginTop: 24,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 12,
          paddingTop: 16,
          borderTop: '1px solid var(--border)',
        }}>
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={loading}
            style={{ minWidth: 220, maxWidth: "100%" }}
          >
            {loading ? (
              <span>{t.common.loading}</span>
            ) : (
              <>
                <Save size={18} />
                {t.common.saveChanges}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
